import {batch, shallowEqual, useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {introAction, menuAction, optionAction, themeAction} from "../../redux/store";
import {getSpecialTypeColorList, productListApi} from "../../../core/api/introApi";
import {getOptionList} from "../../../core/api/optionApis";
import {getPreselectionDetail, getPreselectionList} from "../../../core/api/preselectionApi";
import {stepMgmtDataProcessing} from "../../../core/utils/replace";

import useDidMountEffect from "./useDidMountEffect";
import {setPreselectionVehicleOption} from "../../../core/utils/buildConfigSnippet";
import callPeerRemoteModule from "../../../core/utils/callPeerRemoteModule";
import {elementReady} from "../../../core/utils/tools";
import {platform} from "../../../core/platform/platform";
import TempestWidget from "@tempestcgi/tempest-widget";
import {getCustomersConfigurationList} from "../../components/designHistory/api/designHistoryApi";
import {loadDataAndSet} from "../../redux/optionWindow-slice/optionWindowSlice";


const reg = /[{}[\]/?.,;:|)*~`!^+<>@#$%&\\=('"]/gi;

const useLoadProductData = () => {

  const params = useParams();
  const paramsId = params && params.id && params.id.toLowerCase();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [navAndConfigDataReady, setNavAndConfigDataReady] = useState(true);

  const isMobile = platform.mobile && (platform.ios || platform.safariOnIos || platform.android);
  const mobileSuffix = "_m";

  const langData = useSelector(state => state.theme.langData);
  const {loadingState, CONFIG_DATA, camera, hotspot, connectedMode} = useSelector(state => ({
    loadingState: state.intro.loadingState,
    CONFIG_DATA: state.optionWindow.CONFIG_DATA,
    camera: state.toggle.camera,
    hotspot: state.toggle.hotspot,
    connectedMode: state.theme.connectMode,
  }), shallowEqual);



  const vehicleSelectHandler = async (usersSelectVehicleCode=null, connectMode = "web", configData=null) => {
    const selectedVehicleCode = usersSelectVehicleCode ? usersSelectVehicleCode.toLowerCase() : paramsId && paramsId;

    setNavAndConfigDataReady(false);

    if(!selectedVehicleCode) {
      return;
    }
    dispatch(introAction.setLoadingState("loading"));

    if (connectMode === "remote") {
      await (async () => {
        const peer = await callPeerRemoteModule("remote");
        peer.conn.sendData("webglDestroy");
      })();
    }


    if (connectMode === "web") {
      elementReady("#main-tag").then(res => {
        const dataForInject = {
          canvasId: 'application-canvas',
          assetPrefix: `${process.env.REACT_APP_configurator_url}/${selectedVehicleCode}${isMobile ? mobileSuffix : ""}/`,
          debug: false,
          tools: {"sceneMode": true, cameraPosition: true},
          canvasParentId: "main-tag"
        }

        if (res) {
          TempestWidget.widget(dataForInject);
        }
      });
    }

    getSpecialTypeColorList().then(res => {
      if (res.status === 200 && res.data?.message === "SUCCESS") {
        const specialTypeColorList = res.data.data;
        dispatch(optionAction.setSpecialTypeColorList(specialTypeColorList));
      }
    });

    productListApi().then(res => {
      //product list 에서 사용자가 선택한 product code 와 일치하는 항목의 데이터를 다음 then으로 리턴.
      if (res.data.result && res.data.message === "SUCCESS") {
        const productList = res.data.data.content;
        const selectedProductDetailData = productList.find(item => {
          const productCode = item?.code?.toLowerCase();
          if (productCode === selectedVehicleCode) {
            return item;
          }
          return false;
        });
        return selectedProductDetailData;
      } else {
        return false;
      }
    }).then(async selectedProductDetailData => {
      //위에서 리턴받은 product detail data 의 id를 통해 해당 product 의 navigation list 와 preselection list 를 받아온다.
      if (selectedProductDetailData) {
        const navigationListReq = await getOptionList(selectedProductDetailData.id);
        const preselectionListReq = await getPreselectionList(selectedProductDetailData.id);


        //데이터를 받아오는 데 성공했다면, navigation list 는 가공처리, preselection list 에선 default preselection 을 찾아 리턴.
        if (navigationListReq.data.result && preselectionListReq.data.result) {
          const preselectionList = preselectionListReq.data.data.content || [];
          const defaultPreselection = preselectionList.find(item => item.isDefault);
          const processedNavData = stepMgmtDataProcessing(navigationListReq.data.data, langData, selectedVehicleCode);

          return {selectedProductDetailData, defaultPreselection, processedNavData}
        } else {
          return false;
        }
      } else {
        return false;
      }

    }).then(async (res) => {
      const {defaultPreselection, selectedProductDetailData, processedNavData} = res;
      let defaultPreselectionConfigData;


      const noSavedConfig = (reduxConfigData) => {
        return Object.keys(reduxConfigData).length === 0 && reduxConfigData.constructor === Object;
      }


      async function processDefaultPreselection() {
        if (!defaultPreselection) return {};


        const res = await getPreselectionDetail(selectedProductDetailData.id, defaultPreselection.id);
        if (res.data.result && res.data.message === "SUCCESS") {
          const configData = res.data.data.config_data;
          return await setPreselectionVehicleOption(configData);
        }
        return {};
      }


      async function initializeDefaultConfig() {
        const userRole = sessionStorage.getItem("userRole");
        const customerId = sessionStorage.getItem("userCode");

        if(configData && !noSavedConfig(configData)) {
          return configData;
        } else if (CONFIG_DATA && !noSavedConfig(CONFIG_DATA)) {
          return CONFIG_DATA;
        }

        if (userRole === "COMMON_USER") {
          return await processDefaultPreselection();

        } else if (userRole === "CONFIGURATION_USER") {
          const res = await getCustomersConfigurationList(customerId);
          const { result, message, data } = res.data;

          if (result && message === "SUCCESS") {
            if (data.length > 0) {
              if (data[0].productCode === paramsId) {
                return data[0].configData || {};
              } else {
                return await processDefaultPreselection();
              }
            }
            return await processDefaultPreselection();
          }

        }
        return {};
      }

      await (async () => {
        defaultPreselectionConfigData = await initializeDefaultConfig();
      })();

      return {selectedProductDetailData, processedNavData, defaultPreselectionConfigData}

    }).then(async res => {
      const {selectedProductDetailData, processedNavData, defaultPreselectionConfigData} = res;

      //여러번의 dispatch 가 여러번의 렌더링을 유발할 수 있으므로 batch로 일괄처리 해주는 함수
      const dispatchHandler = async (selectedNav, gnb, connectMode) => {
        await batch(() => {
          const colorwayFromConfig = defaultPreselectionConfigData?.COLORWAY || ""; // null 방지
          // if(colorwayFromConfig) {
          //   dispatch(optionAction.setColorway(colorwayFromConfig));
          // }
          dispatch(menuAction.setMenu({key: "navData", value: processedNavData}));
          dispatch(optionAction.setCurrentVehicle(selectedProductDetailData));
          // dispatch(optionAction.setLoadData(defaultPreselectionConfigData));

          dispatch(loadDataAndSet({processedData: defaultPreselectionConfigData, colorway: colorwayFromConfig}))
          dispatch(optionAction.setCurrentNavigation(selectedNav.replaceAll(reg, "")));
          dispatch(optionAction.setCurrentHeader(selectedNav));
          dispatch(optionAction.setOptions({selection: "isGnb", value: gnb}));
          dispatch(themeAction.setConnectMode(connectMode));
        });
      }

      //기본 사이트와 remote/mediawall 사이트의 경로이동 분기처리 함수
      const pathHandler = async (connectMode, firstNavigationData) => {
        if (connectMode === "web") {
          if(!params["*"]) {

            let path = firstNavigationData.path;
            navigate(path);
          }

        } else if (connectMode === "remote") {
          const peer = await callPeerRemoteModule(connectMode);
          peer.conn.sendData("dispatch", {
            action: "optionAction",
            func: "setCurrentVehicle",
            payload: selectedProductDetailData
          });
          peer.conn.sendData("dispatch", {
            action: "optionAction",
            func: "setCurrentNavigation",
            payload: firstNavigationData.id
          });
          peer.conn.sendData("dispatch", {
            action: "optionAction",
            func: "setCurrentHeader",
            payload: firstNavigationData.id
          });

        }
      }

      const firstNavigationData = processedNavData.navForHeader?.[0] ? processedNavData.navForHeader?.[0] : {
        id: "",
        path: ""
      };
      dispatchHandler(firstNavigationData.id, firstNavigationData.gnb, connectMode);
      pathHandler(connectMode, firstNavigationData);

      setNavAndConfigDataReady(true);
    });
  }


  const resetConfigAndEnv = async () => {
    await dispatch(optionAction.setSendData(CONFIG_DATA));
    await window && window.postMessage({
      msg: "camera:change",
      data: [hotspot, camera.currentCameraType, camera.currentCameraCode]
    }, "*");
  }

  useDidMountEffect(() => {
    if (loadingState === "assetLoad:end") {
      resetConfigAndEnv().then(async () => {
        const remoteModePeerConnected = await callPeerRemoteModule(connectedMode);

        setTimeout(() => {
          dispatch(introAction.setLoadingState("end"));

          if (remoteModePeerConnected) {
            remoteModePeerConnected.conn.sendData("dispatch", {
              action: "introAction",
              func: "setLoadingState",
              payload: "end"
            });
          }
        }, 600);
      });
    }
  }, [loadingState, connectedMode, navAndConfigDataReady]);


  useEffect(() => {
    if(!paramsId) {
      return;
    }
    vehicleSelectHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsId]);


  return {vehicleSelectHandler, navAndConfigDataReady}
}


export default useLoadProductData;
